import {
    type FC
} from "react";
import {
    Pressable,
    View
} from "react-native";
import type ITreeViewMenuButtonProps from "./type";
import stylesheet, {
    getTreeViewMenuButtonMeasures,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    useTreeViewContext
} from "../../context";
import {
    Ellipsis as EllipsisIcon
} from "lucide-react-native";
import {
    TREE_VIEW_PART_TAB_INDEX
} from "../../util";
import StateLayer from "../../../stateLayer";
import Icon from "../../../icon";

const TreeViewMenuButton: FC<ITreeViewMenuButtonProps> = ({
    isDisabled = false,
    onHoverOut,
    onPressOut,
    isVisible,
    onHoverIn,
    onPressIn,
    onFocus,
    onPress,
    onBlur,
    ...props
}) => {
    const {
        customTheme,
        measures
    } = useTreeViewContext();

    const {
        shapeConfigs,
        touchTargets,
        iconSizes,
        radiuses,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        interactionHandlers,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled,
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const isExpanded = props["aria-expanded"] === true;

    const currentMeasures = getTreeViewMenuButtonMeasures({
        touchTargets,
        iconSizes,
        radiuses,
        measures
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        measures: currentMeasures,
        shapeConfigs,
        isVisible
    });

    const isActive = isHovered || isPressed || isExpanded;

    return <Pressable
        {...props}
        {...interactionHandlers}
        tabIndex={TREE_VIEW_PART_TAB_INDEX}
        style={[
            stylesheet.container,
            containerDynamicStyle
        ]}
        disabled={isDisabled}
        aria-hidden={true}
        onPress={onPress}
        focusable={false}
        role="button"
    >
        <View
            style={[
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            <StateLayer
                borderRadius={currentMeasures.radius}
                isPressed={isPressed || isExpanded}
                customTheme={customTheme}
                isHovered={isHovered}
            />
            <Icon
                customColor={isActive ? colors.content.icon.default : colors.content.icon.mid}
                size={currentMeasures.iconSize}
                customTheme={customTheme}
                icon={EllipsisIcon}
            />
        </View>
    </Pressable>;
};
export default TreeViewMenuButton;
